import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { imageUrl } from './api';

interface Props {
  /** The shared order's code, or `load` for any other one (a statement's). */
  orderId?: string;
  /** Resolves to the code image's URL. */
  load?: () => Promise<string>;
  /** Used for the downloaded file name. */
  number: string;
  title?: string;
  hint?: string;
  onClose: () => void;
  notify: (message: string) => void;
}

/**
 * A web page cannot send a mini program card, so the PC shows the order's mini program code:
 * scanning opens the order in the mini program, whose own share sends the card.
 */
export default function ShareCodeDialog(p: Props) {
  const [src, setSrc] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    let url = '';
    (p.load ? p.load() : imageUrl(`/orders/${p.orderId}/mini-code`))
      .then((u) => setSrc((url = u)))
      .catch((e) => setError((e as Error).message));
    return () => {
      if (url.startsWith('blob:')) URL.revokeObjectURL(url);
    };
  }, [p.orderId]);

  async function copy() {
    try {
      // Browsers only put PNG on the clipboard, and WeChat returns JPEG: re-encode first.
      const img = new Image();
      img.src = src;
      await img.decode();
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      canvas.getContext('2d')!.drawImage(img, 0, 0);
      const png = new Promise<Blob>((done, fail) =>
        canvas.toBlob((b) => (b ? done(b) : fail(new Error('encode'))), 'image/png'),
      );
      await navigator.clipboard.write([new ClipboardItem({ 'image/png': png })]);
      p.notify('已复制，可直接粘贴到微信聊天');
    } catch {
      p.notify('浏览器不支持复制图片，请下载后发送');
    }
  }

  function download() {
    const link = document.createElement('a');
    link.href = src;
    link.download = `小程序码-${p.number}.jpg`;
    link.click();
  }

  return (
    <Dialog open fullWidth maxWidth="xs" onClose={p.onClose}>
      <DialogTitle>{p.title ?? '分享给合作方'}</DialogTitle>
      <DialogContent>
        <Box
          sx={{
            height: 220,
            display: 'grid',
            placeItems: 'center',
            mb: 1.5,
          }}
        >
          {error ? (
            <Alert severity="error">{error}</Alert>
          ) : src ? (
            <Box component="img" src={src} alt="小程序码" sx={{ width: 220, height: 220 }} />
          ) : (
            <CircularProgress size={28} />
          )}
        </Box>
        <Typography color="text.secondary" sx={{ fontSize: 13, textAlign: 'center' }}>
          {p.hint ?? '微信扫码打开此订单，在小程序里转发给合作方'}
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button disabled={!src} onClick={download}>
          下载
        </Button>
        <Button variant="contained" disabled={!src} onClick={copy}>
          复制图片
        </Button>
      </DialogActions>
    </Dialog>
  );
}
